import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { observer } from 'mobx-react-lite'
import ButtonWithProgress from 'reusable/mui/progress-button/ButtonWithProgress'
import onEnterProp from 'reusable/utils/on-enter-prop'
import validationConstants from '@gap/utils/validation/validation-constants'
import useStore from 'reusable/mobx-store-utils'
import { useRegionRouterStore } from 'features/main/RegionRouterStore'
import variables from 'variables/variables'
import ArrowRightAltOutlined from '@mui/icons-material/ArrowRightAltOutlined'
import SignUpAdvisorTrialOrgSetupStore, { SignUpAdvisorTrialOrgSetupStoreProvider, useSignUpAdvisorTrialOrgSetupStore } from './SignUpAdvisorTrialOrgSetupStore'
import AuthCardWrapper from 'auth/shared/AuthCardWrapper'
import FileUploadWithProgress, { FileUploadWithProgressProps } from 'reusable/mui/file-uploads/FileUploadWithProgress'
import GapAutocompleteSingle from 'features/gap-components/autocomplete/single/GapAutocompleteSingle'
import { fileValidation, fileValidationDescription } from '@gap/utils/file/file-utils'
import { themeConfig } from '@gap/shared/theme'
import { namingConfigLowerCase } from '@gap/utils/config/naming-config'
import { useSignUpAdvisorStore } from '../../SignUpAdvisorStore'
import { useEffect } from 'react'
import { useAuthStore } from 'auth/AuthStore'
import SkeletonLoadingBox from 'reusable/mui/skeleton/SkeletonLoadingBox'
import CircularProgress from '@mui/material/CircularProgress'
import { IDBMetricsCache } from 'cache/idb/IDBMetricsCache'
import { IDBProcessCache } from 'cache/idb/IDBProcessCache'
import { IDBFavoriteProcessesCache } from 'cache/idb/IDBFavoriteProcessesCache'

export default observer(function SignUpAdvisorTrialOrgSetup() {
   const signUpAdvisorStore = useSignUpAdvisorStore()
   const authStore = useAuthStore()
   const regionRouterStore = useRegionRouterStore()
   const signUpAdvisorTrialOrgSetupStore = useStore(sp => new SignUpAdvisorTrialOrgSetupStore(sp), { regionRouterStore, signUpAdvisorStore, authStore })
   const formHandler = signUpAdvisorTrialOrgSetupStore.formHandler

   useEffect(() => {
      const initialise = async () => {
         // Warm up cache only once fetchTrialOrg has set the org context these queries are scoped by.
         await signUpAdvisorTrialOrgSetupStore.fetchTrialOrg()
         IDBMetricsCache.get()
         IDBProcessCache.getByStatus(['published'])
         IDBFavoriteProcessesCache.get({ favoriteType: 'onboardingFavorite' })
      }
      initialise()
   }, [signUpAdvisorStore, signUpAdvisorTrialOrgSetupStore])

   const isReady = signUpAdvisorTrialOrgSetupStore.pageIsReady

   if (!isReady) {
      return <>
         <Typography variant='body1'>
            Initialising org setup...
         </Typography>
         <CircularProgress size="32px" />
      </>
   }

   return (
      <SignUpAdvisorTrialOrgSetupStoreProvider store={signUpAdvisorTrialOrgSetupStore}>
         <AuthCardWrapper
            title="Set up your organisation"
            subtitle={`Your organisation is where everything lives within ${variables.gapName}. Your team, your meetings, your clients, all in one place.`}
         >
            <Box>

               <Typography variant="body1Bold">Your organisation</Typography>
               <SkeletonLoadingBox length={3} sx={{ height: '56px', mt: `16px!important` }} isLoading={!isReady}>

                  <Box sx={{ mt: 2 }}>
                     {formHandler.name.bindInput(ip => (
                        <TextField
                           autoFocus
                           fullWidth
                           label="Organisation name*"
                           slotProps={{
                              htmlInput: {
                                 maxLength: validationConstants.maxOrgNameLength
                              }
                           }}
                           {...onEnterProp(signUpAdvisorTrialOrgSetupStore.saveOrgData)}
                           {...ip}
                        />
                     ))}
                  </Box>

                  <Box sx={{ mt: 2 }}>
                     {formHandler.website.bindInput(ip => (
                        <TextField
                           fullWidth
                           label="Website"
                           slotProps={{
                              htmlInput: {
                                 maxLength: validationConstants.maxWebsiteLength
                              }
                           }}
                           {...onEnterProp(signUpAdvisorTrialOrgSetupStore.saveOrgData)}
                           {...ip}
                        />
                     ))}
                  </Box>

                  <Box sx={{ mt: 2 }}>
                     {formHandler.countryCode.bindInput(ip => (
                        <GapAutocompleteSingle
                           options={signUpAdvisorTrialOrgSetupStore.countryOptions}
                           label="Country*"
                           {...ip}
                        />
                     ))}
                  </Box>
               </SkeletonLoadingBox>

               <Box sx={{ mt: 3 }}>
                  <Typography variant="body1Bold">Upload a logo</Typography>
                  <SkeletonLoadingBox sx={{ height: '184px', mt: `8px!important` }} isLoading={!isReady}>
                     <Box display="flex" width="100%" justifyContent="space-between" gap={2} sx={{ mt: 2 }}>
                        <Box flex={1}>
                           {signUpAdvisorStore.trialOrg && (
                              <LogoUpload
                                 getPresignedUrl={signUpAdvisorTrialOrgSetupStore.getFileUploadUrl}
                              />
                           )}
                        </Box>

                        {signUpAdvisorTrialOrgSetupStore.showLogoUploadHelperText && (
                           <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                              <Typography variant="body2" sx={{ color: themeConfig.primary[600] }}>
                                 Your logo will be used on any documents you create, such as {namingConfigLowerCase.meetingMinutes.plural}, plans, or reports. You can also upload a logo later from within your organisation settings.
                              </Typography>
                           </Box>
                        )}
                     </Box>
                  </SkeletonLoadingBox>
               </Box>

               <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Box sx={{ flexGrow: 0 }}>
                     <ButtonWithProgress
                        loading={signUpAdvisorTrialOrgSetupStore.saveLoader.isLoading}
                        variant="contained"
                        icon={<ArrowRightAltOutlined />}
                        disabled={!signUpAdvisorTrialOrgSetupStore.canContinue}
                        onClick={signUpAdvisorTrialOrgSetupStore.saveOrgData}
                        sx={{ whiteSpace: 'nowrap' }}
                     >
                        Continue
                     </ButtonWithProgress>
                  </Box>
               </Box>
            </Box>
         </AuthCardWrapper>
      </SignUpAdvisorTrialOrgSetupStoreProvider>
   )
})

const LogoUpload = observer(function LogoUpload(props: { getPresignedUrl: FileUploadWithProgressProps['getPresignedUrl'] }) {
   const signUpAdvisorStore = useSignUpAdvisorStore()
   const signUpAdvisorTrialOrgSetupStore = useSignUpAdvisorTrialOrgSetupStore()

   const org = signUpAdvisorStore.trialOrg
   if (!org) return null

   return (
      <FileUploadWithProgress
         accept={fileValidation.logo.typeMap}
         files={signUpAdvisorTrialOrgSetupStore.existingLogoFile ? [signUpAdvisorTrialOrgSetupStore.existingLogoFile] : []}
         fileOrgId={org.id}
         authUserId={org.userId}
         getPresignedUrl={props.getPresignedUrl}
         subtext={fileValidationDescription.logo}
         getUploadedFileUrl={signUpAdvisorTrialOrgSetupStore.getUploadedLogoUrl}
         maxFiles={1}
         onFilesValidated={signUpAdvisorTrialOrgSetupStore.onFilesValidated}
         onFileRemove={signUpAdvisorTrialOrgSetupStore.onFileRemove}
         onUploadStarted={signUpAdvisorTrialOrgSetupStore.onUploadStarted}
      />
   )
})
